<template>
  <div class="project-detail container">
    <confirm-dialog
      v-model="deleteProjectDialog"
      :selected="selectedConfirm"
      @confirmed="deleteProject"
    ></confirm-dialog>

    <confirm-dialog
      v-model="deleteFileDialog"
      :selected="selectedConfirm"
      @confirmed="deleteFile"
    ></confirm-dialog>

    <confirm-dialog
      v-model="downloadFileDialog"
      title="This file can't be previewed, do you want to download?"
      action="Download"
      icon-class="icon-download"
      :selected="selectedConfirm"
      @confirmed="downloadFile"
    ></confirm-dialog>

    <confirm-dialog
      v-model="stopLiveStreamDialog"
      title="This will stop the live video stream for this project. Continue?"
      action="OK"
      :selected="selectedConfirm"
      @confirmed="stopLiveStream"
    ></confirm-dialog>

    <app-modal
      v-model="uploadFileDialog"
      title="Upload File"
      @close="closeFilePopup()"
    >
      <file-form
        v-model="file"
        :file-to-upload="fileToUpload"
        :selected-file-id="selectedFileId"
        :project-id="project.projectID"
        :active="uploadFileDialog"
        @updated="updatedFile"
        @created="createdFile"
        @start-upload="startUpload"
        @upload-end="uploadEnd"
      ></file-form>
    </app-modal>

    <app-modal
      v-model="liveStreamDialog"
      title="Start Live Stream"
      @close="closePopupLiveStream()"
    >
      <live-stream-form
        v-model="livestream"
        :available-labs="liveStreams"
        :project-id="project.projectID"
        :active="liveStreamDialog"
        @started="liveStreamStarted"
      ></live-stream-form>
    </app-modal>

    <page-header :title="project.name">
      <template v-if="hasPermission([40002])" v-slot:right>
        <div
          v-for="item in customButtons"
          class="level-item"
          :key="item.buttonID"
        >
          <a class="button" :href="item.titleLink" target="_blank">
            <span>{{ item.title }}</span>
          </a>
        </div>
        <div class="level-item">
          <button class="button" @click="openPopupCreateFile">
            <span class="icon icon-upload"></span>
            <span>Upload</span>
          </button>
        </div>
      </template>
    </page-header>

    <upload-progress
      v-model="filesUploading"
      :total="totalFilesToCreate"
    ></upload-progress>

    <div id="filesAndVideosContainer" class="page-content container">
      <app-data-view-header
        :view-by.sync="viewBy"
        :search.sync="search"
        :headers="headers"
        :sort="sortSettings"
        :order-by.sync="orderBy"
        is-small
      >
        <!-- @click:create="openCreateOrganizationPopup" -->
      </app-data-view-header>

      <starred-list
        title="FAVORITE FILES"
        :items="starredFiles"
        @starred="starredFile"
        @open="viewFile"
        :canStarr="canStarred"
      ></starred-list>

      <h1 class="project-section-title">FILES</h1>

      <app-data-viewer
        :hasStarred="canStarred"
        @click:starred="starredFile($event)"
        :grid-has-edit="hasPermission([40001, 40002])"
        :grid-has-delete="hasPermission([40002])"
        grid-has-download
        :loading="loadingFilesList"
        :items="nonVideoFiles"
        :view-by="viewBy"
        :order-by.sync="orderBy"
        :headers="headers"
        @click:grid-item="viewFile($event)"
        @click:list-row="viewFile($event)"
        @click:grid-edit="openEditDialog($event)"
        @click:grid-download="downloadFile($event)"
        @click:grid-delete="
          deleteFileDialog = true;
          selectedConfirm = $event;
        "
      >
        <template
          v-slot:grid.subtitle="{
            createdOnUTC,
            description,
            createdByUserName
          }"
        >
          <span class="grid-subtitle">
            <div class="date">{{ createdOnUTC | toDateStr }}</div>
            <div class="description">{{ description }}</div>
            <div class="upload-by">{{ createdByUserName }}</div>
          </span>
        </template>
        <template v-slot:grid.team="{}">&nbsp;</template>

        <template v-if="canStarred" v-slot:list.item.starred="{ item }">
          <button
            class="button is-text is-icon"
            :class="{ isStarred: item.isStarred }"
            @click.stop="starredFile(item)"
          >
            <span class="icon icon-starred"></span>
          </button>
        </template>
        <template v-slot:list.item.date="{ item }">
          {{ item.createdOnUTC | toDateStr }}
        </template>
        <template v-slot:list.item.actions="{ item }">
          <button
            v-if="hasPermission([40002, 40001])"
            class="button is-text is-icon"
            @click.stop="openEditDialog(item)"
          >
            <span class="icon icon-edit"></span>
          </button>
          <button
            v-if="hasPermission([40002, 40001])"
            class="button is-text is-icon"
            @click.stop="
              deleteFileDialog = true;
              selectedConfirm = item;
            "
          >
            <span class="icon icon-delete"></span>
          </button>
          <button
            class="button is-text is-icon"
            @click.stop="downloadFile(item)"
          >
            <span class="icon icon-download"></span>
          </button>
          <span v-if="$store.getters.userRoleId == 10" class="metainfo"
            >({{ item.downloadCount }})
          </span>
        </template>
      </app-data-viewer>

      <div class="project-section-title">
        <h1 id="research-videos-title">RESEARCH VIDEOS</h1>
        <button
          v-if="hasPermission([40002, 40001])"
          id="start-live-stream-button"
          class="button"
          @click.stop="openPopupLiveStream()"
        >
          Start Live Stream
        </button>
      </div>

      <!-- This special app data viewer for live stream thumbnails is always in grid mode, despite
           the view settings.  This matches the wireframes.  -->
      <div class="live-stream-container">
        <app-data-viewer
          :headers="[]"
          grid-has-expand
          grid-has-stop
          :loading="loadingVideosList"
          :items="projectLiveStreams"
          :view-by="liveStreamsViewBy"
          :suppress-loading-empty="true"
          @click:grid-item="viewVideo($event)"
          @click:grid-expand="viewVideo($event)"
          @click:grid-stop="
            stopLiveStreamDialog = true;
            selectedConfirm = $event;
          "
        >
          <!-- Format the title -->
          <template v-slot:grid.title>
            <div class="grid-title">
              Live Stream
            </div>
          </template>

          <!-- Format the subtitle text -->
          <template v-slot:grid.subtitle="{ name }">
            <div class="livestream-subtitle">
              <div class="description">{{ name }}</div>
            </div>
          </template>

          <template v-slot:grid.team="{}">&nbsp;</template>
        </app-data-viewer>
      </div>

      <app-data-viewer
        :hasStarred="canStarred"
        @click:starred="starredFile($event)"
        grid-has-edit
        grid-has-delete
        grid-has-download
        :loading="loadingVideosList"
        :items="videoFiles"
        :view-by="viewBy"
        :order-by.sync="orderByVideos"
        :headers="headers"
        @click:grid-item="viewVideo($event)"
        @click:list-row="viewVideo($event)"
        @click:grid-edit="openEditDialog($event)"
        @click:grid-download="downloadVideoFromS3($event)"
        @click:grid-delete="
          deleteFileDialog = true;
          selectedConfirm = $event;
        "
      >
        <template
          v-slot:grid.subtitle="{
            createdOnUTC,
            description,
            createdByUserName
          }"
        >
          <span class="grid-subtitle">
            <div class="date">{{ createdOnUTC | toDateStr }}</div>
            <div class="description">{{ description }}</div>
            <div class="upload-by">{{ createdByUserName }}</div>
          </span>
        </template>
        <template v-slot:grid.team="{}">&nbsp;</template>

        <template v-if="canStarred" v-slot:list.item.starred="{ item }">
          <button
            class="button is-text is-icon"
            @click.stop="starredFile(item)"
          >
            <span class="icon icon-starred"></span>
          </button>
        </template>
        <template v-slot:list.item.date="{ item }">
          {{ item.createdOnUTC | toDateStr }}
        </template>
        <template v-slot:list.item.actions="{ item }">
          <button
            v-if="hasPermission([40002, 40001])"
            class="button is-text is-icon"
            @click.stop="openEditDialog(item)"
          >
            <span class="icon icon-edit"></span>
          </button>
          <button
            v-if="hasPermission([40002, 40001])"
            class="button is-text is-icon"
            @click.stop="
              deleteFileDialog = true;
              selectedConfirm = item;
            "
          >
            <span class="icon icon-delete"></span>
          </button>
          <button
            v-if="
              item.processingStatus != 'Uploaded' &&
                item.processingStatus != 'Processing'
            "
            class="button is-text is-icon"
            @click.stop="downloadVideoFromS3(item)"
          >
            <span class="icon icon-download"></span>
          </button>
          <button
            v-if="
              item.processingStatus == 'Uploaded' ||
                item.processingStatus == 'Processing'
            "
            class="button is-text is-icon"
          >
            <span class="upload-spinner"></span>
          </button>
          <span class="metainfo">({{ item.downloadCount }}) </span>
        </template>
      </app-data-viewer>
    </div>

    <download-progress v-model="downloadingFile"></download-progress>
  </div>
</template>

<script>
import AppDataViewHeader from '@/components/AppDataViewHeader.vue';
import AppDataViewer from '@/components/AppDataViewer.vue';
import _ from 'lodash';
import PageHeader from '@/components/PageHeader.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import FileForm from '@/components/forms/FileForm';
import LiveStreamForm from '@/components/forms/LiveStreamForm';
import UploadProgress from '@/components/UploadProgress';
import DownloadProgress from '@/components/DownloadProgress';
import AppModal from '@/components/AppModal';
import { CognitoIdentityCredentials, config, S3 } from 'aws-sdk';
import FilesHelper from '@/mixins/Files.js';
import StarredList from '@/components/StarredList.vue';

// S3 properties
const bucketName = 'vida-vod';
const bucketRegion = 'us-west-2';
const identityPoolId = 'us-west-2:cb03d30c-2a41-48ae-9e34-bb1898375c0f';

export default {
  name: 'ProjectShow',
  components: {
    AppDataViewHeader,
    AppDataViewer,
    ConfirmDialog,
    FileForm,
    LiveStreamForm,
    AppModal,
    PageHeader,
    UploadProgress,
    DownloadProgress,
    StarredList
  },
  mixins: [FilesHelper],
  data() {
    return {
      intervalId: null,
      project: {},
      projectFiles: [],
      editing: false,
      deleteProjectDialog: false,
      deleteFileDialog: false,
      downloadFileDialog: false,
      stopLiveStreamDialog: false,
      selectedConfirm: {},
      selectedFile: -1,
      liveStreams: [],
      liveStreamsViewBy: 'grid',
      headers: [
        {
          text: '',
          value: 'starred',
          sortable: false,
          width: 40,
          align: 'center'
        },
        {
          text: 'Name',
          value: 'name',
          sortable: true,
          customSort: (a, b) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) {
              return -1;
            }
            if (a.name.toLowerCase() > b.name.toLowerCase()) {
              return 1;
            }
            return 0;
          }
        },
        {
          text: 'Date',
          value: 'date',
          sortable: true,
          customSort: (a, b) => {
            if (a.createdOnUTC < b.createdOnUTC) {
              return -1;
            }
            if (a.createdOnUTC > b.createdOnUTC) {
              return 1;
            }
            return 0;
          },
          width: 160,
          align: 'center'
        },
        {
          text: 'Upload by',
          value: 'createdByUserName',
          width: 180
        },
        {
          text: 'Description',
          value: 'description'
        },
        {
          text: '',
          value: 'actions',
          class: 'project-files-table-actions-column',
          align: 'right'
        }
      ],
      sortSettings: [
        {
          text: 'Newest → Oldest',
          value: 'date',
          width: 180,
          sortDirection: -1,
          customSort: (a, b) => {
            if (moment(a.createdOnUTC).isBefore(b.createdOnUTC)) {
              return -1;
            }
            if (moment(a.createdOnUTC).isAfter(b.createdOnUTC)) {
              return 1;
            }
            return 0;
          }
        },
        {
          text: 'Oldest → Newest',
          value: 'date',
          width: 180,
          sortDirection: 1,
          customSort: (a, b) => {
            if (moment(a.createdOnUTC).isBefore(b.createdOnUTC)) {
              return 1;
            }
            if (moment(a.createdOnUTC).isAfter(b.createdOnUTC)) {
              return -1;
            }
            return 0;
          }
        },
        {
          text: 'A → Z',
          value: 'name',
          sortDirection: 1,
          customSort: (a, b) => {
            if (a.__name < b.__name) {
              return 1;
            }
            if (a.__name > b.__name) {
              return -1;
            }
            return 0;
          }
        },
        {
          text: 'Z → A',
          value: 'name',
          sortDirection: -1,
          customSort: (a, b) => {
            if (a.__name < b.__name) {
              return -1;
            }
            if (a.__name > b.__name) {
              return 1;
            }
            return 0;
          }
        }
      ],
      uploadFileDialog: false,
      liveStreamDialog: false,
      uploadType: '', // tracks the type of the currently uploading file: 'Document' or 'Video'
      fileToUpload: null,
      file: {},
      selectedFileId: -1,
      loadingFilesList: true,
      loadingVideosList: true,
      viewBy: 'list',
      orderBy: {},
      orderByVideos: {},
      search: '',
      livestream: {},
      selectedLiveStreamId: -1,
      liveStreams: [],
      customButtons: []
    };
  },
  computed: {
    canStarred: function() {
      return this.hasPermission([40002]);
    },
    starredFiles: function() {
      // Filter to fetch everything that is not a video
      return this.projectFiles.filter(file => {
        return file.isStarred && file.name.includes(this.search);
      });
    },
    nonVideoFiles: function() {
      // Filter to fetch everything that is not a video
      return this.projectFiles.filter(file => {
        return (
          !file.isStarred &&
          file.fileType !== 'Video' &&
          file.name.includes(this.search)
        );
      });
    },
    videoFiles: function() {
      // Filter to fetch the videos
      return this.projectFiles.filter(file => {
        return (
          !file.isStarred &&
          file.fileType === 'Video' &&
          file.name.includes(this.search)
        );
      });
    },
    projectLiveStreams: function() {
      return this.liveStreams.filter(stream => {
        return stream.projectID == this.project.projectID;
      });
    },
    fileHeaders: function() {
      const headersCopy = [...this.headers];
      headersCopy.push({
        text: '',
        value: 'actions',
        class: 'project-documents-table-actions-column',
        align: 'right'
      });
      return headersCopy;
    },
    videoHeaders: function() {
      const headersCopy = [...this.headers];
      headersCopy.push({
        text: '',
        value: 'actions',
        class: 'project-videos-table-actions-column',
        align: 'right'
      });
      return headersCopy;
    }
  },
  mounted() {
    // Create an AWS Cognito identity pool with permissions to write to
    // the S3 bucket
    config.update({
      region: bucketRegion,
      credentials: new CognitoIdentityCredentials({
        IdentityPoolId: identityPoolId
      })
    });

    this.axiosRequest('get', `Projects/${this.$route.params.projectID}`).then(
      response => {
        this.project = response.data;
        // Fetch the current project from the server now, and then periodically.
        // This will allow us to show activity indicators when a video is
        // uploading or processing.
        this.loadVideoFiles();
        this.loadLiveStreams();

        this.customButtons = this.project.buttons;
      }
    );

    this.intervalId = setInterval(() => {
      this.loadVideoFiles();
      this.loadLiveStreams();
    }, 10 * 1000);
  },
  destroyed() {
    if (this.intervalId) clearInterval(this.intervalId);
  },
  methods: {
    starredFile(file) {
      file.isStarred = !file.isStarred;
      for (let i = 0; i < this.projectFiles.length; i++) {
        if (this.projectFiles[i].fileID == file.fileID) {
          this.$set(this.projectFiles, i, file);
          break;
        }
      }

      this.axiosRequest('put', `Files/${file.fileID}`, {
        isStarred: file.isStarred
      })
        .then(() => {
          //save on the store
        })
        .catch(error => {
          this.showErrorMessage(error);
        });
    },
    submit() {
      this.$refs.formproject.validate().then(success => {
        if (!success) {
          this.showErrorMessage('Please check the errors');
          return;
        } else {
          this.axiosRequest(
            'put',
            `Projects/${this.project.projectID}`,
            this.project
          )
            .then(() => {
              this.showSuccessMessage('Project updated successfully');
              this.editing = false;
            })
            .catch(error => {
              this.showErrorMessage(error);
            });
        }
      });
    },
    loadVideoFiles() {
      this.axiosRequest('get', `Projects/${this.$route.params.projectID}/Files`)
        .then(response => {
          this.projectFiles = response.data;
          this.loadingFilesList = false;
          this.loadingVideosList = false;
        })
        .catch(error => {
          this.showErrorMessage(error);
        });
    },
    loadLiveStreams() {
      this.axiosRequest(
        'get',
        `Projects/${this.$route.params.projectID}/VideoLiveStreams`
      ).then(response => {
        this.liveStreams = response.data;
      });
    },
    viewFile(file) {
      let extension = '';
      if (file.fileName)
        extension = file.fileName.substr(file.fileName.lastIndexOf('.') + 1);
      const previewable = ['zip', 'png', 'jpg', 'jpeg', 'gif', 'svg', 'pdf'];

      if (file.fileType == 'Link') {
        this.openLink(file.linkUrl);
      } else if (previewable.includes(extension)) {
        this.$router.push({
          name: 'projects.files.view',
          params: { fileID: file.fileID }
        });
      } else if (['mp4'].includes(extension)) {
        this.viewVideo(file);
      } else {
        this.downloadFileDialog = true;
        this.selectedConfirm = file;
      }
    },
    viewVideo($event) {
      this.$router.push({
        name: 'projects.video',
        params: { videoId: $event.fileID }
      });
    },
    stopLiveStream($event) {
      this.axiosRequest(
        'delete',
        `Projects/${this.project.projectID}/VideoLiveStreams/${this.selectedConfirm.videoLiveStreamID}`
      )
        .then(() => {
          this.stopLiveStreamDialog = false;
        })
        .catch(error => {
          this.showErrorMessage(error);
        });
    },
    deleteProject() {
      this.axiosRequest('delete', `Projects/${this.project.projectID}`)
        .then(() => {
          this.$router.replace({ name: 'projects' });
          this.deleteProjectDialog = false;
        })
        .catch(error => {
          this.showErrorMessage(error);
        });
    },
    deleteFile() {
      this.axiosRequest('delete', `Files/${this.selectedConfirm.fileID}`)
        .then(() => {
          const idx = _.findIndex(this.projectFiles, {
            fileID: this.selectedConfirm.fileID
          });
          this.projectFiles.splice(idx, 1);
          this.deleteFileDialog = false;
        })
        .catch(error => {
          this.showErrorMessage(error);
        });
    },
    downloadFile(file) {
      if (file.fileType == 'Link') {
        this.openLink(file.linkUrl);
        return;
      }

      this.downloadingFile = file;
      this.$set(file, 'downloadPercentage', 0);
      this.axiosRequest('get', `Files/${file.fileID}/Content`, null, {
        responseType: 'blob',
        onDownloadProgress: progressEvent => {
          //console.log(progressEvent.loaded, progressEvent.total);
          const percentage = Math.round(
            (progressEvent.loaded / progressEvent.total) * 100
          );
          this.$set(file, 'downloadPercentage', percentage);
        }
      })
        .then(response => {
          this.downloadFileToBrowser(file, response.data);
        })
        .catch(error => {
          console.log(error);
          this.showErrorMessage('Error is not possible to download file');
        })
        .finally(() => {
          this.downloadFileDialog = false;
          this.downloadingFile = null;
        });
    },
    openPopupCreateFile() {
      console.log('openPopupCreateFile');
      this.uploadFileDialog = true;
      this.selectedFileId = -1;
      this.fileToUpload = null;
      this.file = {};
      this.filesUploading = [];
    },
    updatedFile(file) {
      console.log('updatedFile', file);
      this.closeFilePopup();
      const idx = _.findIndex(this.projectFiles, {
        fileID: this.selectedFileId
      });
      if (idx != -1) {
        this.$set(this.projectFiles, idx, file);
      }
    },
    createdFile(model, nFiles) {
      console.log('createdFile', model);
      this.closeFilePopup();
      if (model.fileType == 'Link') {
        this.projectFiles.push(model);
      } else {
        if (nFiles) this.totalFilesToCreate = nFiles;
        else this.totalFilesToCreate = 1;
      }
    },
    openEditDialog(item) {
      this.fileToUpload = null;
      this.file = item;

      this.selectedFileId = item.fileID;
      this.uploadFileDialog = true;
    },
    closeFilePopup() {
      this.fileToUpload = null;
      this.uploadFileDialog = false;
    },
    openPopupLiveStream() {
      this.liveStreamDialog = true;
      this.livestream = {};
    },
    closePopupLiveStream() {
      this.liveStreamDialog = false;
    },
    liveStreamStarted(model) {
      console.log('liveStreamStarted', model);
      this.closePopupLiveStream();
    }
  }
};
</script>

<style lang="scss" scoped>
.table {
  td:first-child {
    .button {
      margin: 0 auto;
    }
  }

  .icon-starred {
    font-size: 1.8rem !important;
  }
}
.project-files-table-actions-column {
  width: 320px;
}
.upload-progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  width: 100%;
  height: 100%;
  transform-origin: right center;
  border-radius: $radius;
}

.project-detail {
  .page-actions-bar {
    margin-bottom: 40px;
    .level {
      align-items: flex-start;
    }
  }
  .preview-popup {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: #000;
    padding-top: 30px;
    z-index: 10000;

    .icon-close {
      position: absolute;
      top: 10px;
      right: 10px;
      font-size: 2rem;
    }

    iframe {
      width: 100%;
      height: 100%;
    }
  }

  .metainfo {
    color: rgba($text, 0.5);
    font-size: 1.1rem;
    width: 20px;
    display: inline-block;
  }

  .project-videos-table-actions-column {
    width: 320px;
    // @at-root td#{&} {
    //   text-align: right;
    // }

    .cell {
      display: flex;
      justify-content: flex-end;
    }
  }

  #filesAndVideosContainer .app-table {
    min-height: 150px;
    margin-bottom: 10px;
  }

  .grid-subtitle {
    .date {
      font-size: 1.2rem;
      color: #9d9d9c;
    }

    .upload-by {
      font-size: 1.2rem;
    }
  }

  #research-videos-title {
    float: left;
  }

  #start-live-stream-button {
    float: right;
  }

  .live-stream-container .grid-subtitle {
    padding-top: 0;
    padding-bottom: 0;
    margin-top: 40px;
    margin-bottom: -40px;
  }

  .live-stream-container .button span.icon-expand {
    margin-right: 5px;
  }

  .live-stream-container .stop-container {
    margin-top: 105px;
  }
}
</style>
